import { Button } from '@fluentui/react-button';
import {
    makeStyles,
    shorthands,
    tokens,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
} from '@fluentui/react-components';
import { ErrorCircleRegular, EditRegular, MoreHorizontalRegular, DeleteRegular } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-tooltip';
import React, { useState } from 'react';
import { COPY } from '../../../assets/strings';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { deleteConversation } from '../../../redux/features/conversations/conversationsSlice';
import { customTokens } from '../../../styles';
import { DeleteChatDialog } from './dialogs/DeleteChatDialog';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    actionMenu: {
        borderRadius: '8px',
        ...shorthands.padding(tokens.spacingVerticalM),
        lineHeight: '22px',
        minWidth: '160px',
    },
    editButton: {
        padding: 0,
        justifyContent: 'flex-start',
        width: '100%',
    },
    menuItem: {
        padding: tokens.spacingVerticalXS,
        minHeight: '32px',
    },
    menuSeperator: {
        marginBottom: tokens.spacingVerticalS,
        marginTop: tokens.spacingVerticalS,
        backgroundColor: customTokens.colorSeperator,
        height: '1px',
    },
    moreButton: {
        minWidth: '24px',
        width: '24px',
        height: '24px',
    },
});

interface IListItemActionsProps {
    chatId: string;
    onEditTitleClick: () => void;
    agentId?: string;
    onMenuOpenChange?: (open: boolean) => void;
}

export const ListItemActions: React.FC<IListItemActionsProps> = ({
    chatId,
    onEditTitleClick,
    agentId,
    onMenuOpenChange,
}) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    // const [isGettingInvitationId, setIsGettingInvitationId] = useState(false);

    const handleDeleteClick = () => {
        console.log('Delete clicked for chat:', chatId);
        setShowDeleteDialog(true);
    };

    const handleDeleteSuccess = (deletedChatId: string) => {
        // Redux güncellemesini dialog kapandıktan sonra yap
        // Bu şekilde success message gösterilmeden dialog kaybolmaz
        setTimeout(() => {
            dispatch(deleteConversation(deletedChatId));
        }, 3000); // Success message'ın görülmesi ve okunması için 3 saniye bekle
    };

    // Guard against undefined conversation
    const currentConversation = conversations[chatId];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (currentConversation === undefined) {
        return null;
    }

    return (
        <div className={classes.root}>
            {currentConversation.disabled ? (
                <Tooltip content={COPY.CHAT_DELETED_MESSAGE()} relationship="label">
                    <Button
                        icon={<ErrorCircleRegular />}
                        appearance="transparent"
                        aria-label="Alert: Chat has been deleted by another user."
                    />
                </Tooltip>
            ) : (
                <>
                    <Menu
                        onOpenChange={(_e, data) => {
                            if (typeof onMenuOpenChange === 'function') onMenuOpenChange(data.open);
                        }}
                    >
                        <MenuTrigger disableButtonEnhancement>
                            <Button
                                data-testid="moreButton"
                                icon={<MoreHorizontalRegular />}
                                appearance="transparent"
                                aria-label="Chat seçenekleri"
                                size="small"
                            />
                        </MenuTrigger>
                        <MenuPopover className={classes.actionMenu}>
                            <MenuList>
                                <MenuItem
                                    data-testid="renameMenuItem"
                                    onClick={onEditTitleClick}
                                    className={classes.menuItem}
                                >
                                    <EditRegular style={{ marginRight: '8px' }} />
                                    Yeniden adlandır
                                </MenuItem>
                                <div className={classes.menuSeperator}></div>
                                <MenuItem
                                    data-testid="deleteMenuItem"
                                    className={classes.menuItem}
                                    onClick={handleDeleteClick}
                                >
                                    <DeleteRegular
                                        style={{ marginRight: '8px', color: 'var(--colorPaletteRedForeground1)' }}
                                    />
                                    <span style={{ color: 'var(--colorPaletteRedForeground1)' }}>Sil</span>
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>

                    {showDeleteDialog && (
                        <DeleteChatDialog
                            chatId={chatId}
                            agentId={agentId}
                            isOpen={showDeleteDialog}
                            onClose={() => {
                                setShowDeleteDialog(false);
                            }}
                            onSuccess={handleDeleteSuccess}
                        />
                    )}
                </>
            )}
        </div>
    );
};
